import React, { FC, useEffect, useRef, useState } from "react";

import CustomGrid from "ui/grid/CustomGrid";
import {
	GridCellProps,
	GridColumnProps,
	GridItemChangeEvent,
} from "@progress/kendo-react-grid";
import CustomButton from "ui/Button";
import { CLASSNAME, COLOR, ICON_NAMES } from "utils/constant";
import { CustomGridCommandCell } from "ui/grid/GridCommandCell";
import { Comment } from "../utils";
import {
	deleteNoteHeadComment,
	getSalesNoteHeadCommentsApi,
	saveNoteHeadComments,
} from "api/salesNote";
import { t } from "i18next";
import Info from "ui/Info";
import { useInfoStore } from "store/error";
import { TextEditCell } from "ui/grid/TextEditCell";
import StepThreeMobileList from "./StepThreeMobileList";

interface HeadComment extends Comment {
	inEdit?: boolean;
}
export const FormStepThree: FC<{ noteHeaderId: number }> = ({
	noteHeaderId,
}) => {
	const [noteHeadComments, setNoteHeadComments] = useState<HeadComment[]>([]);
	const [isMobileView, setIsMobileView] = useState<boolean>(
		typeof window !== "undefined" ? window.innerWidth < 768 : false
	);

	const deletingLineRef = useRef<HeadComment>();
	const [showDeleteLine, setShowDeleteLine] = useState(false);
	const { setError } = useInfoStore();

	useEffect(() => {
		const handleResize = () => setIsMobileView(window.innerWidth < 768);
		handleResize();
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	useEffect(() => {
		getData();
	}, []);

	const handleOnChangeItem = (e: GridItemChangeEvent) => {
		const item = e.dataItem as HeadComment;
		const description = String(e.value ?? "");
		updateDescriptionLocal(item.id, description);
		updateItem(description, item.id);
	};

	const updateDescriptionLocal = (id: number, description: string) => {
		setNoteHeadComments((prev) =>
			prev.map((line) => (line.id === id ? { ...line, description } : line))
		);
	};

	const handleMobileDescriptionChange = (id: number, description: string) => {
		updateDescriptionLocal(id, description);
	};

	const handleMobileDescriptionBlur = (id: number, description: string) => {
		updateItem(description, id);
	};

	const startDeletingLine = (line: HeadComment) => {
		deletingLineRef.current = line;
		setShowDeleteLine(true);
	};

	const handleDeleteItem = (data: GridCellProps) => {
		startDeletingLine(data.dataItem as HeadComment);
	};

	const deleteLine = () => {
		if (!deletingLineRef.current) return;
		deleteNoteHeadComment(deletingLineRef.current.id)
			.then((response) => {
				getData();
				setShowDeleteLine(!showDeleteLine);
			})
			.catch((err) => {
				if (err?.response?.data) {
					setError(err?.response?.data.returnText);
				} else {
					setError(err.message);
				}
			});
	};

	const gridItems: GridColumnProps[] = [
		{
			field: "lineNumber",
			title: "Lnr",
			width: "40px",
			className: ` no-border`,
			editable: false,
		},
		{
			field: "description",
			title: "Notat",
			editable: true,
			className: ` no-border`,
			headerClassName: "table-text",
			cells: {
				data: (props) => <TextEditCell {...props} field="description" />,
			},
		},
		{
			field: "",
			title: "",
			cells: {
				data: (props) => (
					<CustomGridCommandCell
						{...props}
						style={{ display: "flex", justifyContent: "flex-end" }}
						onclick={handleDeleteItem}
						iconName={ICON_NAMES.Delete}
					/>
				),
			},
			width: "60px",
		},
	];

	const getData = () => {
		getSalesNoteHeadCommentsApi(noteHeaderId)
			.then((response) => {
				setNoteHeadComments(
					response.data.map((line) => ({ ...line, inEdit: true })),
				);
			})
			.catch((err) => {
				if (err?.response?.data) {
					setError(err?.response?.data.returnText);
				} else {
					setError(err.message);
				}
			});
	};

	const addNewItem = () => {
		saveNoteHeadComments({ id: 0, noteHeaderId, description: "" })
			.then((response) => {
				getData();
			})
			.catch((err) => {
				if (err?.response?.data) {
					setError(err?.response?.data.returnText);
				} else {
					setError(err.message);
				}
			});
	};

	const updateItem = (description: string, id: number) => {
		saveNoteHeadComments({ id, noteHeaderId, description })
			.then((response) => {})
			.catch((err) => {
				if (err?.response?.data) {
					setError(err?.response?.data.returnText);
				} else {
					setError(err.message);
				}
			});
	};

	return (
		<div>
			{isMobileView ? (
				<StepThreeMobileList
					comments={noteHeadComments}
					onDescriptionChange={handleMobileDescriptionChange}
					onDescriptionBlur={handleMobileDescriptionBlur}
					onDelete={startDeletingLine}
				/>
			) : (
				<CustomGrid
					gridColumns={gridItems}
					data={noteHeadComments}
					edit={{ id: true }}
					className={CLASSNAME.TableNumeric + " " + CLASSNAME.HideOverFlow}
					onItemChange={handleOnChangeItem}
				/>
			)}
			<div style={{ marginTop: "20px" }}>
				<CustomButton
					label={t("NewLine") + " +"}
					onClick={addNewItem}
					bgColor={COLOR.Green}
					color="white"
				/>
			</div>
			<Info
				close={() => setShowDeleteLine(!showDeleteLine)}
				title={t("DeleteLine")}
				show={showDeleteLine}
				description={t("DeleteLineConfirmation")}
				actions={[
					{ text: t("Cancel"), btn: () => setShowDeleteLine(!showDeleteLine) },
					{
						text: t("Yes"),
						btn: deleteLine,
						bgColor: "red",
						textColor: "white",
					},
				]}
			/>
		</div>
	);
};

export default FormStepThree;
